<template>
  <div class="owc-video-player">
    <ThumbnailComponent :thumbnailData="thumbnail" :thumbnail-id="thumbnailId"></ThumbnailComponent>
    <button
      v-if="!isVideoPlaying"
      class="owc-video-player__play-button"
      @click="handlePlayVideo"
      type="button"
    >
      <wb-icon name="play" class="owc-video-player__play-icon"></wb-icon>
      <span class="owc-video-player__play-text">{{ playerOverlayText }}</span>
    </button>

    <OwcLightbox v-if="openInModal" :isOpen="modalIsOpen" @close="handleCloseLightbox">
      <PlayerComponent
        ref="modalPlayer"
        :playerData="componentData"
        :isModal="true"
        :hasUserInteracted="hasUserInteracted"
        @onReady="handlePlayerReady"
        @trackEvent="handleShieldVideoTracking"
      ></PlayerComponent>
    </OwcLightbox>
    <PlayerComponent
      v-else
      v-show="isVideoPlaying"
      :playerData="componentData"
      :hasUserInteracted="hasUserInteracted"
      @onReady="handlePlayerReady"
      @trackEvent="handleShieldVideoTracking"
    ></PlayerComponent>
  </div>
</template>

<script lang="ts">
// TODO: must stop video on slide change
/**
 * `VideoPlayer` - This video component will be placed in all components that require a video media block.
 * The component can play HTML5 and Youtube videos, it uses Plyr library to manage the video settings and controls.
 * It contains a thumbnail and the player, it can be played inside a modal or after the play button is clicked behind
 * the thumbnail element.
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { VideoCustomEvent, VideoPlayerComponent, VideoPlayerMixinsDataJSON } from './VideoPlayer';
import ThumbnailComponent from './Thumbnail.vue';
import PlayerComponent from './Player.vue';
import Plyr, { PlyrEvent, StandardEvent } from 'plyr';
import { CUSTOM_EVENTS, PLYR_EVENTS } from './VideoPlayer.config';
import OwcLightbox from '@owc-packages/Lightbox/src/Lightbox.vue';
import { getComponentStateConnection } from '@owc-utils/Store/owcComponent';

import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import Logger from '../../_utils/common/Logger';

export default (ComponentInitializeDispatcher as VideoPlayerComponent).extend({
  name: 'owc-video-player',
  components: {
    ThumbnailComponent,
    PlayerComponent,
    OwcLightbox,
  },
  mixins: [OwcDataComponent],
  data() {
    return {
      modalIsOpen: false,
      player: null,
      isVideoPlaying: false,
      isHeightSet: false,
      hasUserInteracted: false,
      playerReady: false,
    };
  },
  beforeCreate() {
    const isPrivacyShieldDefined = customElements.get('cmm-privacy-shield') !== undefined;
    if (isPrivacyShieldDefined) return; // was already loaded

    if (Object.hasOwn(globalThis, 'importShim')) {
      // import module using es-module-shims npm package
      globalThis.importShim('@cmp/privacy-shield').catch(error => {
        // module doesn't exist in importmap
        Logger.error(`Module @cmp/privacy-shield failed to import`, error);
      });
    } else if (Object.hasOwn(globalThis, 'importModule')) {
      globalThis.importModule('@cmp/privacy-shield').catch(error => {
        // module doesn't exist in importmap
        Logger.error(`Module @cmp/privacy-shield failed to import`, error);
      });
    }
  },
  mounted() {
    if (this.componentData) {
      this.setThumbnailData(this.componentData);
    }
  },
  computed: {
    thumbnailId: function() {
      return this.componentId ? `${this.componentId}-thumbnail` : null;
    },
    videoDetails: function() {
      return this.componentData?.videoDetails;
    },
    thumbnail: function() {
      return this.componentData?.thumbnail;
    },
    playerOverlayText: function() {
      return this.componentData?.playerOverlayText;
    },
    openInModal: function() {
      return Boolean(this.videoDetails?.openInModal);
    },
    isDarkMode: function() {
      return Boolean(this.componentData?.darkMode);
    },
  },
  methods: {
    buildEventPayload() {
      return {
        detail: {
          playerId: this.componentId,
        },
        composed: true,
        bubbles: true,
      };
    },
    async setThumbnailData(data: VideoPlayerMixinsDataJSON) {
      if (this.thumbnailId && this.thumbnail) {
        const payload = {
          id: this.thumbnailId,
          name: 'owc-video-player-thumbnail',
          json: {
            height: data?.height,
            genericImage: {
              ...this.thumbnail,
            },
          },
        };
        const connection = await getComponentStateConnection();
        connection.updateComponent(payload);
      }
    },
    handlePlayerReady(player: Plyr) {
      this.playerReady = true;
      this.player = player;

      this.player.on(PLYR_EVENTS.ended as StandardEvent, (): void => {
        this.isVideoPlaying = false;
        if (this.openInModal) {
          this.modalIsOpen = false;
        }
      });
      const eventPayload = this.buildEventPayload();
      this.player.on(PLYR_EVENTS.play as StandardEvent, () => {
        this.$el.dispatchEvent(new CustomEvent(CUSTOM_EVENTS.start, eventPayload));
      });
      this.player.on(PLYR_EVENTS.pause as StandardEvent, () => {
        this.$el.dispatchEvent(new CustomEvent(CUSTOM_EVENTS.stop, eventPayload));
      });
      this.$el.dispatchEvent(new CustomEvent<VideoCustomEvent>(CUSTOM_EVENTS.ready, eventPayload));

      this.handleVideoTracking();

      //This is for the first play when the user accepts the consent
      this.playVideo();
    },
    handlePlayVideo(): void {
      this.hasUserInteracted = true;
      this.isVideoPlaying = true;

      if (this.openInModal && !this.modalIsOpen) {
        this.modalIsOpen = true;
      }
      // Else, player is initialized and play starts from there
      if (this.playerReady) {
        this.playVideo();
      }
    },
    handleCloseLightbox(): void {
      this.modalIsOpen = false;
      this.isVideoPlaying = false;

      // There should be a way to stop privacy shield video playing, however, we can't at the time

      if (this.player) {
        this.player.pause();
      }
      this.$el.dispatchEvent(new CustomEvent(CUSTOM_EVENTS.stop, this.buildEventPayload()));
    },
    playVideo(): void {
      if (this.player) {
        this.player?.playing ? this.player.pause() : this.player?.play();
      }
    },
    handleShieldVideoTracking(ev?: CustomEvent): void {
      let action = '';
      let percentagePlayed = 0;
      switch (ev?.type) {
        case 'privacy-shield-video-play':
          action = 'video_youtube.video_start';
          break;
        case 'privacy-shield-video-percentage-played':
          percentagePlayed = ev.detail.videoPercentagePlayed;
          if (percentagePlayed >= 25 && percentagePlayed < 50) {
            percentagePlayed = 25;
          } else if (percentagePlayed >= 50 && percentagePlayed < 90) {
            percentagePlayed = 50;
          } else {
            percentagePlayed = 90;
          }
          action = `video_youtube.video_${percentagePlayed}`;
          break;
        default:
          break;
      }
      this.handleCustomTrackingDetailed(
        this.$el,
        {
          label: ev?.detail.videoTitle || this.componentData?.title,
          category: 'video',
          action,
        },
        'click',
      );
    },
    handleVideoTracking(): void {
      const provider = this.player?.provider;
      this.handleCustomTrackingDetailed(
        this.$el,
        {
          label: this.player?.source.title || this.componentData?.title,
          category: 'video',
          action: `video_${provider}.video_start`,
        },
        'click',
      );

      const shouldTrackAndDetach = (plyr: Plyr, percentageInterval: number[]): boolean => {
        const currentTime = plyr.currentTime;
        const currentPercent = (100 * currentTime) / plyr.duration;
        if (currentPercent < percentageInterval[0] || currentPercent > percentageInterval[1]) {
          return false;
        }

        this.handleCustomTrackingDetailed(
          this.$el,
          {
            label: plyr.source.title || this.componentData?.title,
            category: 'video',
            action: `video_${plyr.provider}.video_${percentageInterval[0]}`,
          },
          'click',
        );
        return true;
      };
      const currentTimeCallback25 = (ev: PlyrEvent) => {
        if (shouldTrackAndDetach(ev.detail.plyr, [25, 49])) {
          ev.detail.plyr.off('timeupdate', currentTimeCallback25);
        }
      };
      const currentTimeCallback50 = (ev: PlyrEvent) => {
        if (shouldTrackAndDetach(ev.detail.plyr, [50, 89])) {
          ev.detail.plyr.off('timeupdate', currentTimeCallback25);
          ev.detail.plyr.off('timeupdate', currentTimeCallback50);
        }
      };
      const currentTimeCallback90 = (ev: PlyrEvent) => {
        if (shouldTrackAndDetach(ev.detail.plyr, [90, 100])) {
          ev.detail.plyr.off('timeupdate', currentTimeCallback25);
          ev.detail.plyr.off('timeupdate', currentTimeCallback50);
          ev.detail.plyr.off('timeupdate', currentTimeCallback90);
        }
      };
      this.player?.on('timeupdate', currentTimeCallback25);
      this.player?.on('timeupdate', currentTimeCallback50);
      this.player?.on('timeupdate', currentTimeCallback90);
    },
  },
  watch: {
    componentData(data) {
      //FIXME: backwards compatibility - remove on refactor to OWC_COMPONENT_REGISTRY
      if (this.componentId && data && data.height && !this.isHeightSet) {
        this.isHeightSet = true;
        this.setThumbnailData(data);
      }
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/z-index-list';

.owc-video-player {
  position: relative;

  cursor: pointer;

  &__play-button {
    position: absolute;
    top: 50%;
    left: 50%;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    margin: 0;
    padding: 0;
    border: 0;

    background-color: transparent;

    transform: translate(-50%, -50%);

    cursor: pointer;
  }
  &__spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 1;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    margin: 0;
    padding: 0;
    border: 0;

    background-color: transparent;

    transform: translate(-50%, -50%);
  }
  &__play-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 8px;
    // Using 1rem because wb buttons are in experimental phase, we will change it when they are
    // updated to the correct format.
    margin-bottom: 1rem;

    &::before {
      position: absolute;
      z-index: $z-index-negative;

      width: 40px;
      height: 40px;

      border-radius: 50%;

      background: var(--wb-white);
      box-shadow: 0px 2px 4px 0px;

      content: '';
    }
  }
  &__play-text {
    @include wb-type-copy-secondary;

    color: var(--wb-white);
  }
}
</style>
